// brain.ts — the AI client. Calls the LIVE Oriora server brain for this app:
//   https://api.orioralabs.com/api/apps/ai_health_tracker_coach/{recap|ask|parse-label}
// with the customer's Oriora session token. The server holds the prompts + bills the
// Oriora wallet (Managed AI / BYOK). Food + weight data stays local; only a small
// DERIVED slice (HealthSignals — totals, deltas, streaks; see signals.rs) is sent per
// call. Raw per-meal rows and notes never leave the device this way. (The photo→calories
// path is separate — see estimateMealPhoto below, POST /estimate-photo — but it also goes
// through the Oriora brain, not device-direct; there is no vendor key on this device.)
//
// ONE billed AI call per user action. CRUD, not agentic: scoped JSON in, text out.
// No agent loop, no 2nd call, no tool-use chain, no direct OpenAI/Anthropic SDK here.
//
// HARDEST guardrails (health vertical — the strictest fence in the fleet):
//   • Observational/descriptive ONLY over the user's OWN log. Calorie numbers are
//     ESTIMATES and are labeled so. NEVER diagnosis, medical advice, diet prescriptions,
//     meal plans, weight-loss targets, or body judgement.
//   • MEDICAL fence: a hard PRE-LLM refusal (no billed call) for medical/advice
//     questions — see medicalRefusal() below + the matching server-side prompt rules.

import { getAccessToken } from "./supabase";
import { invoke } from "./tauri-web";
import type { HealthSignals } from "./store";

const SERVER = "https://api.orioralabs.com";
const APP_SLUG = "ai_health_tracker_coach";

export type BrainErrorCode =
  | "insufficient_funds"
  | "managed_ai_blocked"
  | "unauthenticated"
  | "unavailable"
  | "refused_medical"
  | "no_store"
  | "too_large";

export class BrainError extends Error {
  code: BrainErrorCode;
  constructor(code: BrainErrorCode, message: string) {
    super(message);
    this.code = code;
    this.name = "BrainError";
  }
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

// SSE reader for the streaming fleet chat (request body {stream:true} → text/event-stream):
// `: open` / `: keepalive` comments and [DONE] are ignored, each `data: {"chunk":…}` event
// feeds onChunk, and the `data: {"done":true, …}` event carries the exact old JSON payload —
// returned where res.json() used to be, so all existing action/reply mapping stays untouched.
async function readSseChat(bodyStream: ReadableStream<Uint8Array>, onChunk: (t: string) => void): Promise<Record<string, unknown>> {
  const reader = bodyStream.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  let done: Record<string, unknown> | null = null;
  for (;;) {
    const { value, done: eof } = await reader.read();
    if (eof) break;
    buf += decoder.decode(value, { stream: true });
    let nl: number;
    while ((nl = buf.indexOf("\n")) !== -1) {
      const line = buf.slice(0, nl).replace(/\r$/, "");
      buf = buf.slice(nl + 1);
      if (!line.startsWith("data:")) continue; // comment (": open"/": keepalive") or blank line
      const payload = line.slice(5).trim();
      if (payload === "[DONE]") continue;
      let ev: Record<string, unknown>;
      try { ev = JSON.parse(payload) as Record<string, unknown>; } catch { continue; }
      if (typeof ev.chunk === "string") onChunk(ev.chunk);
      else if (ev.done) done = ev;
    }
  }
  if (!done) throw new BrainError("unavailable", "The reply stream ended early — please try again.");
  // A streamed reply can end with a billing/auth error instead of a payload ({done:true, error, _status}).
  // Returning it as data dissolved the "Top up" message into an empty action (found live 2026-08-27) —
  // throw the same BrainError the buffered path would.
  if (typeof done.error === "string" && done.error) {
    const st = typeof done._status === "number" ? done._status : 0;
    const msg = typeof done.message === "string" && done.message ? done.message : done.error;
    if (st === 401) throw new BrainError("unauthenticated", msg);
    if (st === 402) throw new BrainError("insufficient_funds", msg);
    if (st === 403) throw new BrainError("managed_ai_blocked", msg);
    throw new BrainError("unavailable", msg);
  }
  return done;
}

export async function brainCall<T>(endpoint: string, body: unknown, onChunk?: (t: string) => void): Promise<T> {
  const token = await getAccessToken();
  if (!token) throw new BrainError("unauthenticated", "Please sign in to Oriora.");
  // Retry transient failures (502/503/504 + network). Hard errors (401/402/403) never retry.
  const MAX = 3;
  for (let attempt = 1; attempt <= MAX; attempt++) {
    let res: Response;
    try {
      res = await fetch(`${SERVER}/api/apps/${APP_SLUG}/${endpoint}`, {
        method: "POST",
        headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
        body: JSON.stringify(onChunk && body && typeof body === "object" ? { ...(body as Record<string, unknown>), stream: true } : body),
      });
    } catch {
      if (attempt < MAX) { await sleep(attempt * 800); continue; }
      throw new BrainError("unavailable", "AI is temporarily unavailable.");
    }
    if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
    if (res.status === 402) throw new BrainError("insufficient_funds", "Add funds to keep using AI.");
    if (res.status === 403) throw new BrainError("managed_ai_blocked", (await res.json().catch(() => null))?.message || "Connect a vendor key in Oriora settings to use AI for this app.");
    if ([502, 503, 504].includes(res.status)) {
      if (attempt < MAX) { await sleep(attempt * 800); continue; }
      throw new BrainError("unavailable", "AI is busy — please try again.");
    }
    if (!res.ok) throw new BrainError("unavailable", "AI request failed.");
    // Streaming fleet chat: with onChunk the body carries stream:true and the server answers
    // as SSE — read it incrementally (readSseChat above); the done event IS the old JSON
    // payload. Anything that is not an event stream (old server, an error status body, a
    // proxy that buffered it away) falls through to the buffered path — never a dead end.
    if (onChunk && res.body && (res.headers.get("content-type") ?? "").includes("text/event-stream")) {
      return (await readSseChat(res.body, onChunk)) as unknown as T;
    }
    return (await res.json()) as T;
  }
  throw new BrainError("unavailable", "AI is temporarily unavailable.");
}

// ── MEDICAL fence (hard, PRE-LLM) ───────────────────────────────────────────────
// Meal Tracker describes YOUR OWN logged numbers; it is NOT a doctor, dietitian, or
// coach. Any question that reaches for diagnosis, treatment, medication, a condition,
// a diet prescription, a meal plan, a target, or "should I…" is refused BEFORE any
// billed call, pointing to a professional. (Mirrored server-side as a second line.)
// The fence itself now lives in fence.ts so a battery can exercise it without pulling in
// Tauri/Supabase. Re-exported here so every existing caller is unchanged.
export { MEDICAL_PATTERNS, MEDICAL_REFUSAL, medicalRefusal } from "./fence";
import { medicalRefusal } from "./fence";

export interface RecapAnswer {
  /** The observational recap over the user's own log. */
  recap: string;
}

/**
 * Weekly recap — billed feature #1. We compute the slice on-device
 * (store.computeSignals / signals.rs) and send ONLY that HealthSignals object. The brain
 * narrates the pre-computed numbers ("You logged 16 meals across 6 days this week,
 * averaging about 1,900 kcal per logged day — 8% lower than the week before…").
 * Purely observational; estimates labeled; never advice. Endpoint:
 * POST /api/apps/ai_health_tracker_coach/recap  (Bearer JWT, bills the wallet).
 */
export async function weeklyRecap(signals: HealthSignals): Promise<RecapAnswer> {
  if (signals.total_meals === 0) {
    throw new BrainError("unavailable", "Log a few meals first — there's nothing in your log yet.");
  }
  const r = await brainCall<Partial<RecapAnswer>>("recap", { signals });
  return { recap: String(r.recap ?? "").trim() };
}

export interface AskAnswer {
  answer: string;
}

/**
 * Ask about your log — billed feature #2. Question + the SAME computed signals →
 * an answer grounded ONLY in those figures. The medical fence runs here (pre-call,
 * no billed call for medical asks) AND in the server prompt.
 * Endpoint: POST /api/apps/ai_health_tracker_coach/ask.
 */
export async function askHealth(signals: HealthSignals, question: string): Promise<AskAnswer> {
  const q = question.trim();
  const refusal = medicalRefusal(q);
  if (refusal) throw new BrainError("refused_medical", refusal);
  if (!q) throw new BrainError("unavailable", "Type a question about your log first.");
  if (signals.total_meals === 0 && signals.weight.entry_count === 0) {
    throw new BrainError("unavailable", "Log a few meals first — there's nothing in your log yet.");
  }
  const r = await brainCall<Partial<AskAnswer>>("ask", { signals, question: q });
  return { answer: String(r.answer ?? "").trim() };
}

export interface ParsedLabel {
  name: string;
  calories: number | null;
  protein_g: number | null;
  carbs_g: number | null;
  fat_g: number | null;
}

/**
 * Label-scan FALLBACK — only offered when the LOCAL parse (label.ts) fails. Sends the
 * on-device OCR TEXT of the nutrition label (never the photo) for ONE billed extraction
 * call — pure number-lifting, no commentary. Endpoint: POST …/parse-label.
 */
export interface MealEstimateResult { name: string; calories: number | null; protein_g: number | null; carbs_g: number | null; fat_g: number | null; confidence: string | null }
/** THE ESTIMATOR: meal description → rough calorie/macro estimate (one billed call, /estimate). */
export async function estimateMeal(description: string): Promise<MealEstimateResult> {
  const d = description.trim().slice(0, 800);
  if (!d) throw new BrainError("unavailable", "Describe the meal first.");
  let r = await brainCall<Partial<MealEstimateResult>>("estimate", { description: d });
  // parse_failed = the server couldn't read the model's reply (Pattern 10) — a transient model
  // flake (reproduced 1-in-N live, 2026-08-28; the SAME description then parsed 3/3). One quiet
  // retry before bothering the user: their single ask deserves a second attempt.
  if ((r as { parse_failed?: boolean }).parse_failed) r = await brainCall<Partial<MealEstimateResult>>("estimate", { description: d });
  if ((r as { parse_failed?: boolean }).parse_failed) throw new BrainError("unavailable", "The reading failed — try again.");
  const numOrNull = (v: unknown): number | null =>
    typeof v === "number" && Number.isFinite(v) && v >= 0 ? v : null;
  return {
    name: typeof r.name === "string" ? r.name : "",
    calories: numOrNull(r.calories),
    protein_g: numOrNull(r.protein_g),
    carbs_g: numOrNull(r.carbs_g),
    fat_g: numOrNull(r.fat_g),
    confidence: typeof r.confidence === "string" ? r.confidence : null,
  };
}

/** Photo → estimate through the ORIORA BRAIN (POST /estimate-photo). The router picks an
 *  image-capable model — no device key, the photo never re-serves. Returns the app MealEstimate. */
export async function estimateMealPhoto(photoDataUrl: string): Promise<import("./store").MealEstimate> {
  const r = await brainCall<Partial<MealEstimateResult>>("estimate-photo", { image: photoDataUrl });
  // Same parse_failed signal as /estimate — the reading failed, not "nothing in the photo".
  if ((r as { parse_failed?: boolean }).parse_failed) throw new BrainError("unavailable", "The reading failed — try again.");
  const numOrNull = (v: unknown): number | null =>
    typeof v === "number" && Number.isFinite(v) && v >= 0 ? v : null;
  const conf = r.confidence === "high" || r.confidence === "medium" || r.confidence === "low" ? r.confidence : "low";
  return {
    meal_name: typeof r.name === "string" ? r.name : "",
    calories_estimate: numOrNull(r.calories) ?? 0,
    protein_g: numOrNull(r.protein_g),
    carbs_g: numOrNull(r.carbs_g),
    fat_g: numOrNull(r.fat_g),
    confidence: conf,
  };
}

export async function parseLabelWithAI(text: string): Promise<ParsedLabel> {
  const t = text.trim().slice(0, 6000);
  if (!t) throw new BrainError("unavailable", "No label text to read.");
  const r = await brainCall<Partial<ParsedLabel>>("parse-label", { text: t });
  // Same parse_failed signal as /estimate — the extraction failed, not "the label has no numbers".
  if ((r as { parse_failed?: boolean }).parse_failed) throw new BrainError("unavailable", "The reading failed — try again.");
  const numOrNull = (v: unknown): number | null =>
    typeof v === "number" && Number.isFinite(v) && v >= 0 ? v : null;
  return {
    name: typeof r.name === "string" ? r.name : "",
    calories: numOrNull(r.calories),
    protein_g: numOrNull(r.protein_g),
    carbs_g: numOrNull(r.carbs_g),
    fat_g: numOrNull(r.fat_g),
  };
}

// ── Cloud backup (the user owns their data) ─────────────────────────────────────
// The device builds the FAITHFUL backup locally (export_backup); these calls relay it to the
// user's OWN connected cloud store — their GitHub repo or Google Drive (connected once in the
// Oriora account/Connections hub; the server picks whichever). Oriora RETAINS nothing. Plain
// HTTP → works on iOS with no file picker. Endpoints: POST/GET /api/apps/ai_health_tracker_coach/backup.
export interface CloudBackupState { connected: boolean; provider?: string; data?: unknown }

async function authedFetch(endpoint: string, init?: RequestInit): Promise<Response> {
  const token = await getAccessToken();
  if (!token) throw new BrainError("unauthenticated", "Please sign in to Oriora.");
  return fetch(`${SERVER}/api/apps/${APP_SLUG}/${endpoint}`, {
    ...init,
    headers: { ...(init?.headers ?? {}), authorization: `Bearer ${token}` },
  });
}

/** Save the local backup envelope to the user's connected cloud store. Throws BrainError('no_store')
 *  when nothing is connected, so the UI can point them at the Connections hub. */
export async function cloudBackupSave(envelope: unknown): Promise<{ provider: string }> {
  let res: Response;
  try {
    res = await authedFetch("backup", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ data: envelope }),
    });
  } catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (res.status === 409) throw new BrainError("no_store", "Connect GitHub or Google Drive in your Oriora account to back up to the cloud.");
  // 413 = over the standard path's size limit (photo-heavy backup). Not retryable — the caller
  // switches to the device-direct GitHub save, or tells the customer how to enable it.
  if (res.status === 413) throw new BrainError("too_large", "This backup is too large for the standard path.");
  if (!res.ok) throw new BrainError("unavailable", "Cloud backup failed — please try again.");
  return (await res.json()) as { provider: string };
}

/** Save arbitrary text — a note the user dictates, or the whole conversation — to the user's OWN cloud
 *  store (their GitHub/Drive). Honors an explicit destination when the user names one. Oriora retains
 *  nothing (text relay only). Throws no_store when nothing is connected. Endpoint: the shared
 *  POST /api/apps/ai_health_tracker_coach/store route (distinct from the whole-log `backup` route). */
export async function cloudStoreSave(
  content: string,
  opts?: { name?: string; kind?: string; destination?: "github" | "gdrive" },
): Promise<{ provider: string }> {
  let res: Response;
  try {
    res = await authedFetch("store", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ content, name: opts?.name, kind: opts?.kind, provider: opts?.destination }),
    });
  } catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (res.status === 409) throw new BrainError("no_store", "Connect GitHub or Google Drive in your Oriora account to save to the cloud.");
  if (!res.ok) throw new BrainError("unavailable", "Saving to your cloud failed — please try again.");
  return (await res.json()) as { provider: string };
}

/** Read the cloud backup for restore. `connected:false` when no store is connected.
 *  Pass `provider` to read a NAMED source (the RESTORE STORY's explicit restore) — the server
 *  409s rather than silently reading a different cloud than the one the customer asked for. */
export async function cloudBackupLoad(provider?: "github" | "gdrive"): Promise<CloudBackupState> {
  let res: Response;
  try { res = await authedFetch(provider ? `backup?provider=${provider}` : "backup"); }
  catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (res.status === 409) throw new BrainError("no_store", `${provider === "gdrive" ? "Google Drive" : "GitHub"} isn't connected in your Oriora account.`);
  if (!res.ok) throw new BrainError("unavailable", "Couldn't read your cloud backup.");
  return (await res.json()) as CloudBackupState;
}

/** The RESTORE STORY status line — which clouds are connected, which one backups WRITE to, and the
 *  dated backup each holds. Ground truth from the relay (CUSTOMER-CLOUD-STORAGE.md §THE RESTORE STORY). */
export interface CloudBackupSlot { backup_exists: boolean; updated_at: string | null; bytes: number | null }
export interface CloudBackupStatusInfo {
  connected: ("github" | "gdrive")[];
  writes_to: "github" | "gdrive" | null;
  slots: Partial<Record<"github" | "gdrive", CloudBackupSlot>>;
}
export async function cloudBackupStatus(): Promise<CloudBackupStatusInfo> {
  let res: Response;
  try { res = await authedFetch("backup/status"); }
  catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (!res.ok) throw new BrainError("unavailable", "Couldn't check your backup status.");
  return (await res.json()) as CloudBackupStatusInfo;
}

// Login opens the ecosystem web login INSIDE the app (its own WebKit window), not the OS
// browser — the in-app https handback is robust on iOS where the custom-scheme deep-link is
// fragile (mirrors expense_audit_ai). Session returns via auth:deep-link (App.tsx + ipc.rs).
// The web callback must allowlist `ai-health-tracker-coach` as a return target (operator task).
export const openWebLogin = () => invoke<void>("open_login_window");
